// [ --- TYPES --- ]
export type Ok<T> = { ok: true; value: T };
export type Err<E> = { ok: false; error: E };

export type Result<T, E> = Ok<T> | Err<E>;

// [ --- CONSTRUCTORS --- ]
export const ok = <T>(value: T): Result<T, never> => ({ ok: true, value });
export const err = <E>(error: E): Result<never, E> => ({ ok: false, error });

// [ --- TYPE GUARDS --- ]
export const isOk = <T, E>(r: Result<T, E>): r is Ok<T> => r.ok;
export const isErr = <T, E>(r: Result<T, E>): r is Err<E> => !r.ok;

// [ --- UTILITIES --- ]
export const unwrap = <T, E>(r: Result<T, E>): T => {
  if (isOk(r)) return r.value;
  throw new Error(`[SILOREAL][SDK] Tried to unwrap an Err: ${r.error}`);
};
export const unwrapErr = <T, E>(r: Result<T, E>): E => {
  if (isErr(r)) return r.error;
  throw new Error(`[SILOREAL][SDK] Tried to unwrapErr an Ok: ${r.value}`);
};
export const unwrapOr = <T, E>(r: Result<T, E>, fallback: T): T => {
  return isOk(r) ? r.value : fallback;
};
export const unwrapOrElse = <T, E>(r: Result<T, E>, fn: (error: E) => T): T => {
  return isOk(r) ? r.value : fn(r.error);
};

// [ --- INTERNAL UTILITIES --- ]
export const _match = <T, E, ROk, RErr>(
  r: Result<T, E>,
  patterns: {
    ok: (value: T) => ROk;
    err: (error: E) => RErr;
  }
): ROk | RErr => {
  return isOk(r) ? patterns.ok(r.value) : patterns.err(r.error);
};
